import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Form, FormProps, Space } from 'antd';
import { InfoCircleFilled, RightOutlined } from '@ant-design/icons';
import { BottomButton, BottomFooter, FormContainer } from './styles';
import ShopeeCNSCGuide, { ShopTypeEnum } from './ShopeeCNSCGuide';
import DepartmentItemSelect from './DepartmentItemSelect';
import checkedSvg from '../assets/checked-blue.svg';
import { useTranslate } from '@tolgee/react';
import { RegionCheckbox } from './RegionCheckbox';
import { AuthScopeHint } from './AuthScopeHint';
import { useChatDiversionDialog } from 'components/ChatDiversionDialog/useChatDiversionDialog';
import { useAuthorizationPolicy } from '../hooks/useAuthorizationPolicy';
import { authScopeDisabledRegions, authScopePlatformAllowed } from '../utils/authScope';

export interface ShopeeShopFormProps {
  form: FormProps['form'];
  initialShopType?: ShopTypeEnum;
  initialRegion?: General.UpperCaseRegion;
  onNext?: () => void;
  showNext?: boolean;
}

export const regionOptions: General.UpperCaseRegion[] = [
  'PH',
  'TH',
  'ID',
  'MY',
  'VN',
  'SG',
  'BR',
  'MX',
  'CO',
  'PL',
  'ES',
  'CL',
  'FR',
  'AR',
];

const ShopeeShopType = (props: ShopeeShopFormProps) => {
  const { form, showNext = true, onNext, initialShopType, initialRegion } = props;
  const [shopType, setShopType] = useState<ShopTypeEnum>();
  const [region, setRegion] = useState<General.UpperCaseRegion | 'CNSC'>('PH');
  const { t } = useTranslate();
  const { show: showChatDiversionDialog } = useChatDiversionDialog();
  const { policy } = useAuthorizationPolicy();

  const disabledRegions = useMemo(
    () => authScopeDisabledRegions(policy, 'shopee', regionOptions),
    [policy],
  );
  const platformAllowed = authScopePlatformAllowed(policy, 'shopee');

  const shopTypeSelect = useMemo(() => {
    const options = [
      {
        value: ShopTypeEnum.CrossOfficial,
        label: t('authShop.跨境店铺'),
      },
      {
        value: ShopTypeEnum.Cross3PF,
        label: t('authShop.thirdPF店铺'),
      },
      {
        value: ShopTypeEnum.Local,
        label: t('ViewShops.LOCAL'),
      },
    ];

    return (
      <Space>
        {options.map((option) => {
          const { value, label } = option;
          return (
            <ShopType
              key={value}
              className={shopType === value ? 'checked' : ''}
              onClick={() => {
                setShopType(value);

                switch (value) {
                  case ShopTypeEnum.CrossOfficial: {
                    form?.setFieldsValue({
                      shopType: value,
                      storeType: 'CROSS',
                      region: 'CNSC',
                    });
                    setRegion('CNSC');
                    break;
                  }
                  case ShopTypeEnum.Cross3PF: {
                    form?.setFieldsValue({
                      shopType: value,
                      storeType: 'CROSS',
                      region: 'PH',
                    });
                    setRegion('CNSC');
                    break;
                  }
                  case ShopTypeEnum.Local: {
                    form?.setFieldsValue({
                      shopType: value,
                      storeType: 'LOCAL',
                      region: regionOptions.includes(region as General.UpperCaseRegion)
                        ? region
                        : 'PH',
                    });
                    setRegion(
                      regionOptions.includes(region as General.UpperCaseRegion) ? region : 'PH',
                    );
                    break;
                  }
                  default: {
                    break;
                  }
                }
                form?.setFieldsValue({ account: '', password: '' });
              }}
            >
              {label}
            </ShopType>
          );
        })}
      </Space>
    );
  }, [form, shopType, region, t]);

  const regionVisible = useMemo(() => {
    return shopType === ShopTypeEnum.Local;
  }, [shopType]);

  const btnDisabled = useMemo(() => {
    if (!shopType) return true;
    if (regionVisible && !region) return true;
    if (!platformAllowed) return true;
    return regionVisible && disabledRegions.includes(region as General.UpperCaseRegion);
  }, [region, regionVisible, shopType, platformAllowed, disabledRegions]);

  useEffect(() => {
    const shopType = initialShopType || form?.getFieldValue('shopType');
    const region = initialRegion || form?.getFieldValue('region');
    setShopType(shopType);
    setRegion(region);
  }, [form, initialShopType, initialRegion]);

  return (
    <FormContainer layout="vertical" form={form}>
      <Form.Item label={t('authShop.请选择您要添加的店铺类型')} name="shopType" required>
        {shopTypeSelect}
        <div className="flex items-center bg-[var(--fett-surface-warning-bg)] px-2 mt-2">
          <InfoCircleFilled style={{ color: 'var(--ant-warning-color)' }} size={14} />
          <span className="ml-2">{t('authShop.shopeeAuthTips2')}</span>
          <Button type="link" rel="noreferrer" onClick={() => showChatDiversionDialog(shopType)}>
            {t('feedback.查看详情')}
          </Button>
        </div>
      </Form.Item>
      {regionVisible ? (
        <>
          {disabledRegions.length > 0 ? <AuthScopeHint /> : null}
          <Form.Item label={t('authShop.请选择店铺站点')} name="region" required>
            <RegionCheckbox options={regionOptions} disabledOptions={disabledRegions} />
          </Form.Item>
        </>
      ) : null}
      {shopType && <ShopeeCNSCGuide shopType={shopType} />}
      {shopType && <DepartmentItemSelect platform="shopee" />}
      {showNext && (
        <BottomFooter>
          <BottomButton
            title={t('authShop.请先选择您要添加的店铺类型')}
            disabled={btnDisabled}
            size="large"
            type="primary"
            onClick={onNext}
          >
            {t('authShop.下一步')}
            <RightOutlined />
          </BottomButton>
        </BottomFooter>
      )}
    </FormContainer>
  );
};

const ShopType = styled.div`
  display: inline-block;
  min-width: 120px;
  height: 40px;
  line-height: 40px;
  padding: 0 26px;
  text-align: center;
  border: 1px solid var(--fett-border-strong);
  cursor: pointer;

  &:hover,
  &.checked {
    color: var(--ant-primary-color);
    border-color: var(--ant-primary-color);
  }
  &.checked {
    background: url(${checkedSvg}) no-repeat right bottom;
  }
`;

export default ShopeeShopType;
